/**
 * The Executive Overview (WALLIYO-0132), built to the approved design.
 *
 * An Executive does not work a queue. They read the shape of the month and
 * click into whichever department the shape is about — so this is a band of
 * headline figures carrying their own movement, one line per department with
 * that department's own headline, the tension between two figures called out,
 * and where the work is actually happening.
 */

import type { DepartmentLine, ExecutiveOverview as Data, HeroFigure } from "@wallyo/contracts/console/overview";

import { t, tCount, type MessageId } from "../../i18n";
import { formatNumber } from "../../i18n/format";

const figureLabel = (id: string) => t(`overview.hero.${id}` as MessageId);

/**
 * How a figure moved, and whether that is good news.
 *
 * A share is said in points: 68% to 61% fell seven points, and calling that
 * "down 10%" is a different and wronger sentence.
 */
function Movement({ figure }: { figure: HeroFigure }) {
  if (figure.change === null || figure.change === 0) return null;
  const up = figure.change > 0;
  const good = up === figure.upIsGood;
  const size = Math.abs(figure.change);
  const said =
    figure.changeIn === "points"
      ? tCount("overview.points", size, { points: formatNumber(size) })
      : figure.changeIn === "people"
        ? // Bare, as the design has it: "↑ 9" under "Active pros" needs no unit,
          // because the figure above it already said what is being counted.
          formatNumber(size)
        : `${formatNumber(size)}%`;
  return (
    <div className={good ? "hero-up" : "hero-down"}>
      {up ? "↑" : "↓"} {said}
    </div>
  );
}

export function ExecutiveOverviewView({ data }: { data: Data }) {
  return (
    <>
      {/* The band. Dark, so the company's numbers are not one card among many. */}
      <div className="exec-hero">
        {data.hero.map((figure) => (
          <div key={figure.figureId}>
            <div className="exec-hero-n">{figure.display}</div>
            <div className="exec-hero-c">{figureLabel(figure.figureId)}</div>
            <Movement figure={figure} />
          </div>
        ))}
      </div>

      <div className="split list">
        <section className="card">
          <div className="role-block-head">
            <h2>{t("overview.eachDepartment")}</h2>
            <span className="foot">{t("overview.clickThrough")}</span>
          </div>
          <ul className="hr-plain">
            {data.departments.map((line) => (
              <DepartmentRow key={line.department} line={line} />
            ))}
          </ul>
        </section>

        <div className="exec-side">
          {data.watch ? (
            <section className="card exec-watch">
              <h2>{t("overview.numberToWatch")}</h2>
              <p className="foot">
                {t(`overview.watch.${data.watch.id}` as MessageId, {
                  first: formatNumber(data.watch.first),
                  second: formatNumber(data.watch.second),
                })}
              </p>
            </section>
          ) : null}

          <section className="card">
            <h2>{t("overview.whereWalliyoWorks")}</h2>
            {data.cities.length === 0 ? (
              <p className="foot">{t("overview.noCitiesYet")}</p>
            ) : (
              <ul className="hr-plain">
                {data.cities.map((city) => (
                  <li key={city.city} className="exec-city">
                    <span>{city.city}</span>
                    <div className="grow" />
                    <span className="mono">{city.jobs}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

/** One department, one line, and the dot that says how it is doing. */
function DepartmentRow({ line }: { line: DepartmentLine }) {
  return (
    <li className="exec-dept">
      <a href={line.href}>
        <span className={`exec-dot ${line.tone}`} aria-hidden="true" />
        <span className="exec-dept-name">{t(`overview.dept.${line.department}` as MessageId)}</span>
        <span className={line.tone === "bad" ? "foot danger" : line.tone === "warn" ? "foot warn-text" : "foot"}>
          {t(
            // Zero is not a number to print here: "nothing overdue" is what the
            // design says, and "0 overdue" is what a machine says.
            (line.secondCount === 0
              ? `overview.headline.${line.headlineId}.zero`
              : `overview.headline.${line.headlineId}`) as MessageId,
            {
              count: String(line.count),
              second: String(line.secondCount ?? 0),
            },
          )}
        </span>
      </a>
    </li>
  );
}
